import { Component, inject, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import {
  AvatarConfig,
  LogoConfig,
  NavigationItem,
  NavigationService,
} from '../../core/navigation/navigation.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Theme, ThemeService } from '../../core/theme/theme.service';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import type { AuthConfigService, AuthService, UserRole } from '@angular-monorepo/auth';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { ThemeSelectorComponent } from '../theme/theme.component';
import { TranslateModule } from '@ngx-translate/core';
import { I18N_FACADE, I18nComponent, I18nFacade } from '@angular-monorepo/i18n';
import {
  FooterConfig,
  LayoutConfigService,
} from '../../services/navigation/footer-sidebar.service';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { Popover } from 'primeng/popover';

@Component({
  selector: 'lib-ui-sidebar',
  imports: [
    CommonModule,
    ButtonModule,
    AvatarModule,
    RouterLink,
    MenuModule,
    DrawerModule,
    DividerModule,
    TagModule,
    ThemeSelectorComponent,
    I18nComponent,
    TranslateModule,
    SelectModule,
    FormsModule,
    Popover,
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css',
  standalone: true,
})
export class SidebarComponent implements OnInit, OnDestroy {
  footerConfig: FooterConfig = { showDefault: true };
  footerAppName = 'la aplicación';
  footerEnvironmentLabel = 'Development';
  footerEnvironmentSeverity: 'success' | 'warn' | 'info' | 'contrast' = 'info';
  @Input() navigation: NavigationItem[] = [];

  @Input() logo: LogoConfig = {
    type: 'image',
    src: 'assets/logo.png',
    alt: 'Logo',
  };

  sidebarOpen = false;
  desktopSidebarCollapsed = false;
  sanitizedSvgContent: SafeHtml | undefined;
  currentTheme: Theme = 'light';
  userName: string | null = null;

  accentColor: string | undefined;
  topBarBgColor: string | undefined;
  searchQuery = '';
  avatarConfig: AvatarConfig = { type: 'icon', value: 'pi pi-user' };
  userRole: string | null = null;
  userRoles: UserRole[] = [];
  activeRole: UserRole | null = null;
  showRoleDropdown = false;

  userNavigation: MenuItem[] = [];
  // Estado de expansión para submenús
  expanded = new Set<string>();
  private readonly subscriptions: Subscription[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly navigationService = inject(NavigationService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly themeService = inject(ThemeService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly layoutConfigService = inject(LayoutConfigService);
  private readonly i18n = inject<I18nFacade>(I18N_FACADE);
  private authService: AuthService | null = null;
  private authConfigService: AuthConfigService | null = null;

  constructor() {
    this.logo = this.navigationService.logo;
    this.navigation = this.navigationService.navigation;
    this.sanitizeSvgContent();
    this.currentTheme = this.themeService.currentThemeValue;
  }

  ngOnInit() {
    this.navigationService.setCurrentNavigationItem(this.router.url);

    const customUserNav = this.navigationService.userNavigation;
    if (customUserNav && customUserNav.length > 0) {
      this.userNavigation = customUserNav;
    } else {
      this.initializeUserNavigation();
    }

    this.navigationService.userNavigationChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((userNavigation) => {
        this.userNavigation = userNavigation;
      });

    this.i18n.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const currentCustomNav = this.navigationService.userNavigation;
      if (!currentCustomNav || currentCustomNav.length === 0) {
        this.initializeUserNavigation();
      }
    });

    const routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.navigationService.setCurrentNavigationItem(event.url);
        this.markActiveAndExpand();
      });
    this.subscriptions.push(routerSub);

    const logoSub = this.navigationService.logoChanges.subscribe((logo) => {
      this.logo = logo;
      this.sanitizeSvgContent();
    });
    this.subscriptions.push(logoSub);

    const navSub = this.navigationService.navigationChanges.subscribe((navItems) => {
      this.navigation = navItems;
    });
    this.subscriptions.push(navSub);

    const themeSub = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
    });
    this.subscriptions.push(themeSub);

    this.loadAuthServices();

    this.layoutConfigService.footerConfig$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
      this.footerConfig = config;
      this.footerAppName = this.resolveFooterAppName(config.appName);
      const env = this.normalizeEnvironment(config.environment);
      this.footerEnvironmentLabel = env.label;
      this.footerEnvironmentSeverity = env.severity;
    });

    this.navigationService.accentColorChanges.pipe(takeUntil(this.destroy$)).subscribe((color) => {
      this.accentColor = color;
    });

    this.navigationService.topBarBgColorChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((color) => {
        this.topBarBgColor = color;
      });

    this.navigationService.avatarConfigChanges.pipe(takeUntil(this.destroy$)).subscribe((config) => {
      this.avatarConfig = config;
    });

    this.markActiveAndExpand();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToProfile(): void {
    this.router.navigate(['/profile']).then((r) => r);
  }

  navigateToSettings(): void {
    this.router.navigate(['/settings']).then((r) => r);
  }

  toggle(id?: string) {
    if (!id) return;
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
    } else {
      this.expanded.add(id);
    }
  }

  isExpanded(id?: string) {
    return !!id && this.expanded.has(id);
  }

  toggleDesktopSidebar(): void {
    this.desktopSidebarCollapsed = !this.desktopSidebarCollapsed;
  }

  getDesktopLogoImageClass(): string {
    const baseClass = this.logo.class || 'mx-auto h-12 w-auto';
    const collapsedAutoClass = this.desktopSidebarCollapsed
      ? ' max-h-12 max-w-12 object-contain'
      : '';
    return `${baseClass}${collapsedAutoClass}`;
  }

  onRoleChange(role: UserRole | number | string | null): void {
    if (role == null || !this.authService) {
      return;
    }

    const resolvedRole =
      typeof role === 'object' ? role : this.userRoles.find((item) => item.id === Number(role));

    if (!resolvedRole) {
      return;
    }

    this.activeRole = resolvedRole;
    this.authService.setActiveRole(resolvedRole);
  }

  private initializeUserNavigation(): void {
    this.userNavigation = [
      {
        label: this.i18n.instant('common.logout'),
        icon: 'pi pi-sign-out',
        command: () => {
          this.authService?.logout();
        },
      },
    ];
  }

  private async loadAuthServices(): Promise<void> {
    // Dynamic import to break UI ↔ auth circular dependency
    const { AuthService, AuthConfigService } = await import('@angular-monorepo/auth');
    this.authService = this.injector.get(AuthService);
    this.authConfigService = this.injector.get(AuthConfigService);

    this.loadAuthUser();
    this.loadUserRoles();
  }

  private loadUserRoles(): void {
    if (!this.authService || !this.authConfigService) return;
    if (this.authConfigService.getConfig().enableOwnRolesRequest === false) return;

    const rolesSub = this.authService.getOwnRoles().subscribe((roles) => {
      this.userRoles = roles;
      this.showRoleDropdown = roles.length > 1;
      this.activeRole = roles[0] ?? null;

      if (this.activeRole && this.authService) {
        this.authService.setActiveRole(this.activeRole);
      }
    });
    this.subscriptions.push(rolesSub);
  }

  private loadAuthUser(): void {
    if (!this.authService) return;
    const userSub = this.authService.getUser().subscribe((user: unknown) => {
      const u = (user ?? {}) as Record<string, unknown>;
      const firstName = (u['first_name'] as string) || '';
      const lastName = (u['last_name'] as string) || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        this.userName = fullName;
      } else {
        const raw = ((u['name'] as string) || (u['username'] as string) || '').toString();
        this.userName = raw
          ? raw
              .replace(/[._]/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
          : null;
      }
      const roles = u['roles'] as string[] | undefined;
      const rawRole = roles?.[0] || (u['role'] as string | undefined) || null;
      const roleLabels: Record<string, string> = { admin: 'Administrador', user: 'Usuario' };
      this.userRole = rawRole ? roleLabels[rawRole] || rawRole : null;
    });
    this.subscriptions.push(userSub);
  }

  private sanitizeSvgContent() {
    if (this.logo.type === 'svg' && this.logo.svgContent) {
      this.sanitizedSvgContent = this.sanitizer.bypassSecurityTrustHtml(this.logo.svgContent);
    }
  }

  getAvatarLabel(): string {
    if (this.avatarConfig.type === 'initials' && this.userName) {
      const parts = this.userName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return this.userName.substring(0, 2).toUpperCase();
    }
    return '';
  }

  private resolveFooterAppName(appName?: string): string {
    if (appName?.trim()) {
      return appName.trim();
    }

    const logoAlt = this.logo?.alt?.trim();
    if (logoAlt && logoAlt.toLowerCase() !== 'logo') {
      return logoAlt;
    }

    return 'la aplicación';
  }

  private normalizeEnvironment(environment?: string): {
    label: string;
    severity: 'success' | 'warn' | 'info' | 'contrast';
  } {
    const raw = (environment || '').toLowerCase().trim();

    if (raw === 'production' || raw === 'prod') {
      return { label: 'Producción', severity: 'success' };
    }

    if (raw === 'qa' || raw === 'staging' || raw === 'stage') {
      return { label: raw === 'qa' ? 'QA' : 'Staging', severity: 'warn' };
    }

    if (raw === 'development' || raw === 'dev') {
      return { label: 'Development', severity: 'info' };
    }

    if (raw) {
      return { label: environment as string, severity: 'contrast' };
    }

    const host = globalThis?.location?.hostname?.toLowerCase() || '';
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return { label: 'Development', severity: 'info' };
    }
    if (host.includes('qa') || host.includes('staging')) {
      return { label: host.includes('qa') ? 'QA' : 'Staging', severity: 'warn' };
    }

    return { label: 'Producción', severity: 'success' };
  }

  private markActiveAndExpand(): void {
    const url = this.router.url;

    const visit = (items: NavigationItem[]): boolean => {
      let anyActiveInLevel = false;

      items?.forEach((item) => {
        const selfActive = !!item.href && (url === item.href || url.startsWith(item.href + '/'));
        let childActive = false;

        if (item.children?.length) {
          childActive = visit(item.children);
          const groupKey = item.id || item.href;
          if (groupKey && childActive) {
            this.expanded.add(groupKey);
          }
        }

        item.current = selfActive || childActive;
        anyActiveInLevel = anyActiveInLevel || item.current;
      });

      return anyActiveInLevel;
    };

    visit(this.navigation as NavigationItem[]);
  }
}
